import React from 'react'
import { useSelector } from 'react-redux'
import { Box, Flex, Text } from '@chakra-ui/core'
import dayjs from 'dayjs'
import { Activity, ArrowLeft, ArrowRight, Check, CheckCheck, Clock, Repeat, Users } from 'lucide-react'
import { RootState } from 'constants/interfaces'
import { isRtlSelector } from 'redux/selectors/general'
import { notifTokens, notifShadows } from 'services/notifications/designTokens'
import { getNotificationVisual } from 'services/notifications/serviceVisuals'
import { getStatusTone } from 'services/notifications/designTokens'
import { INotification } from 'services/notifications/types'
import { resolveNotificationTitle } from 'services/notifications/notificationTitle'
import { getNotificationContextId, getOccurrenceLabel } from 'services/notifications/notificationReferenceFields'
import { Button, Capsule } from 'components/ui'
import LocaleText from 'components/LocaleText'
import ContextDisplay from 'components/ContextDisplay'
import useLocaleText from 'components/useLocaleText'

// Backend sends a real user id for a person-triggered event, or a non-user marker like "app" for
// system/scheduled events (deadline reminders, etc.) — those never resolve through ContextDisplay.
const SYSTEM_SENDER_IDS = new Set(['app', 'system'])

const formatEventType = (eventType: string): string =>
  eventType
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

// One row of the metadata list: a small tinted icon, a micro-label, and either a value string or
// a custom node (e.g. a resolved ContextDisplay avatar+name for the Context row).
const MetaField: React.FC<{ icon: React.ReactNode; label: string; value?: string; children?: React.ReactNode }> = ({
  icon,
  label,
  value,
  children,
}) => (
  <Flex style={{ columnGap: '10px' }} alignItems="flex-start" minW="0">
    <Flex
      alignItems="center"
      justifyContent="center"
      w="26px"
      h="26px"
      borderRadius="8px"
      bg="#F5F6F8"
      color={notifTokens.muted}
      flexShrink={0}
      mt="1px"
    >
      {icon}
    </Flex>
    <Box minW="0" flex="1">
      <Text
        fontSize="10.5px"
        fontWeight={600}
        color={notifTokens.faint}
        letterSpacing="0.04em"
        textTransform="uppercase"
      >
        {label}
      </Text>
      {children ?? (
        <Text fontSize="13px" fontWeight={600} color={notifTokens.ink} mt="1px" isTruncated>
          {value}
        </Text>
      )}
    </Box>
  </Flex>
)

interface IProps {
  notification: INotification | null
  onOpen: (notification: INotification) => void
}

const formatTimestamp = (ts: number, todayLabel: string) => {
  const d = dayjs.unix(ts)
  return d.isSame(dayjs(), 'day') ? `${todayLabel}, ${d.format('HH:mm')}` : d.format('D MMM, HH:mm')
}

// Column 3 of the desktop modal (spec §2) — the point of the whole surface: it shows the target
// entity the way its own service shows it. Re-renders fully on row selection; when `preview` is
// absent from the notification (most services today — only service_calls mock data carries one),
// falls back to just the title/body per the data contract.
export const NotificationPreviewColumn: React.FC<IProps> = ({ notification, onOpen }) => {
  const locale = useSelector((state: RootState) => state.general.locale)
  const isRtl = useSelector(isRtlSelector)
  const translations = useSelector((state: RootState) => state.config.translations)
  const t_new_content_pop_btn = useLocaleText('new_content_pop_btn')
  const t_select_notification = useLocaleText('select_notification')
  const t_occurrence = useLocaleText('occurrence')
  const t_read_at = useLocaleText('read_at')
  const t_context = useLocaleText('context')
  const t_event = useLocaleText('event')

  if (!notification) {
    return (
      <Flex flex="1" minW="0" alignItems="center" justifyContent="center" bg={notifTokens.canvas} h="100%">
        <Text fontSize="13px" color={notifTokens.faint}>
          {t_select_notification}
        </Text>
      </Flex>
    )
  }

  const visual = getNotificationVisual(notification, isRtl)
  const serviceLabel = translations?.[locale]?.[visual.label] ?? visual.label
  const todayLabel = translations?.[locale]?.['t_today'] ?? 'Today'
  const title = resolveNotificationTitle(notification, translations?.[locale] as Record<string, string>, serviceLabel)
  const { preview } = notification
  const occurrenceLabel = getOccurrenceLabel(notification)
  const contextId = getNotificationContextId(notification)
  const isSystemSender = !notification.sender_id || SYSTEM_SENDER_IDS.has(notification.sender_id)

  return (
    <Flex flex="1" minW="0" flexDirection="column" bg={notifTokens.canvas} h="100%">
      {/* Header bar */}
      <Flex alignItems="center" justifyContent="space-between" bg="transparent" p="16px 22px" flexShrink={0}>
        <Flex alignItems="center" style={{ columnGap: '10px' }}>
          <Box
            display="inline-flex"
            alignItems="center"
            borderRadius="6px"
            border={`1px solid ${visual.border}`}
            bg={visual.soft}
            color={visual.text}
            px="8px"
            py="3px"
            fontSize="11.5px"
            fontWeight={500}
          >
            <LocaleText text={visual.label} />
          </Box>
          <Text fontSize="12px" color={notifTokens.faint}>
            {formatTimestamp(notification.created_at_ts, todayLabel)}
          </Text>
        </Flex>
      </Flex>

      {/* Body */}
      <Box
        flex="1"
        minH="0"
        overflowY="auto"
        p="20px 22px 24px"
        style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
      >
        <Text fontSize="19px" fontWeight={600} color={notifTokens.ink} letterSpacing="-0.025em">
          {title}
        </Text>

        {/* Details — generalizes across every service instead of depending on the (rarely
            populated) `preview` field: who sent it, what kind of event it was, its read state,
            and its resolved context. Raw entity ids (tid/oid/cid/service_call_id/...) are
            intentionally never shown here — they mean nothing to a person reading this. */}
        <Box
          bg="white"
          border={`1px solid ${notifTokens.hairline}`}
          borderRadius="16px"
          boxShadow={notifShadows.card}
          p="18px"
        >
          <Flex alignItems="center" justifyContent="space-between">
            {isSystemSender ? (
              <Flex alignItems="center" style={{ columnGap: '9px' }}>
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  w="32px"
                  h="32px"
                  borderRadius="999px"
                  bg="#EEF2F7"
                  color={notifTokens.muted}
                  flexShrink={0}
                >
                  <Clock size={15} />
                </Flex>
                <Text fontSize="13px" fontWeight={600} color={notifTokens.ink}>
                  System
                </Text>
              </Flex>
            ) : (
              <ContextDisplay
                contextId={notification.sender_id}
                showContextName
                fontSize="13px"
                fontWeight={600}
                textColor={notifTokens.ink}
                avatarProps={{ width: '32px', height: '32px' }}
                gap={9}
              />
            )}

            {notification.seen ? (
              <Capsule label="Read" color="#15803D" icon={<Check size={10} />} />
            ) : (
              <Capsule label="t_unread_msgs_btn" color="#B45309" dot />
            )}
          </Flex>

          {notification.body && (
            <Text fontSize="13.5px" fontWeight={500} lineHeight="20px" color={notifTokens.bodyText} mt="8px" pl="41px">
              {notification.body}
            </Text>
          )}

          <Box
            mt="16px"
            pt="16px"
            borderTop={`1px solid ${notifTokens.hairline}`}
            display="grid"
            style={{ gridTemplateColumns: '1fr 1fr', rowGap: '16px', columnGap: '14px' }}
          >
            <MetaField
              icon={<Activity size={13} />}
              label={t_event}
              value={formatEventType(notification.event_type) || '—'}
            />

            {occurrenceLabel && <MetaField icon={<Repeat size={13} />} label={t_occurrence} value={occurrenceLabel} />}

            {notification.seen && notification.seen_at_ts && (
              <MetaField
                icon={<CheckCheck size={13} />}
                label={t_read_at}
                value={formatTimestamp(notification.seen_at_ts, todayLabel)}
              />
            )}

            {contextId && (
              <Box
                gridColumn={occurrenceLabel || (notification.seen && notification.seen_at_ts) ? '1 / -1' : undefined}
              >
                <MetaField icon={<Users size={13} />} label={t_context}>
                  <ContextDisplay
                    contextId={contextId}
                    showContextName
                    fontSize="13px"
                    fontWeight={600}
                    textColor={notifTokens.ink}
                    avatarProps={{ width: '20px', height: '20px' }}
                    gap={6}
                    containerProps={{ mt: '2px' }}
                  />
                </MetaField>
              </Box>
            )}
          </Box>

          <Flex justifyContent="flex-end" mt="18px">
            <Button
              variant="primary"
              onClick={() => onOpen(notification)}
              h="36px"
              minW="auto"
              px="18px"
              style={{ columnGap: '6px' }}
              borderRadius="999px"
              bg={notifTokens.ink}
              color="white"
              fontSize="12.5px"
              fontWeight={600}
              boxShadow="0 1px 2px rgba(15,23,42,.08), 0 6px 16px rgba(15,23,42,.14)"
              _hover={{ bg: '#1E293B', transform: 'none' }}
              _active={{ bg: '#020617', transform: 'none', boxShadow: 'none' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
            >
              {t_new_content_pop_btn}
              {isRtl ? <ArrowLeft size={14} /> : <ArrowRight size={14} />}
            </Button>
          </Flex>
        </Box>

        {preview && (
          <Box bg="white" border={`1px solid ${notifTokens.border}`} borderRadius="12px" p="16px">
            <Flex alignItems="center" justifyContent="space-between" mb="10px">
              <Flex alignItems="center" style={{ columnGap: '8px' }}>
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  w="22px"
                  h="22px"
                  borderRadius="6px"
                  bg={visual.soft}
                  color={visual.text}
                >
                  {visual.icon}
                </Flex>
                <Text
                  fontSize="11px"
                  fontWeight={600}
                  color={notifTokens.faint}
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                >
                  {preview.typeLabel}
                </Text>
              </Flex>
              {(() => {
                const tone = getStatusTone(preview.status.tone)
                return (
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    borderRadius="6px"
                    border={`1px solid ${tone.border}`}
                    bg={tone.soft}
                    color={tone.text}
                    px="8px"
                    py="3px"
                    fontSize="11px"
                    fontWeight={500}
                  >
                    {preview.status.label}
                  </Box>
                )
              })()}
            </Flex>

            <Text fontSize="14.5px" fontWeight={600} color={notifTokens.ink} mb="12px">
              {preview.objectTitle}
            </Text>

            <Box display="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '11px 22px' }}>
              {preview.fields.slice(0, 6).map((field) => (
                <Box key={field.label} minW="0">
                  <Text fontSize="11.5px" color={notifTokens.faint}>
                    {field.label}
                  </Text>
                  <Text fontSize="13px" fontWeight={500} color={notifTokens.ink} isTruncated>
                    {field.value}
                  </Text>
                </Box>
              ))}
            </Box>

            {preview.tags.length > 0 && (
              <Flex
                mt="14px"
                pt="12px"
                borderTop={`1px solid ${notifTokens.hairline}`}
                style={{ gap: '6px' }}
                flexWrap="wrap"
              >
                {preview.tags.map((tag) => (
                  <Box
                    key={tag}
                    borderRadius="6px"
                    bg={notifTokens.rowHover}
                    border={`1px solid ${notifTokens.hairline}`}
                    color={notifTokens.bodyText}
                    px="8px"
                    py="3px"
                    fontSize="11px"
                    fontWeight={500}
                  >
                    {tag}
                  </Box>
                ))}
              </Flex>
            )}
          </Box>
        )}

        {preview && preview.timeline.length > 0 && (
          <Box bg="white" border={`1px solid ${notifTokens.border}`} borderRadius="12px" p="16px">
            <Text
              fontSize="11px"
              fontWeight={600}
              color={notifTokens.faint}
              letterSpacing="0.08em"
              textTransform="uppercase"
              mb="12px"
            >
              What happened
            </Text>
            {preview.timeline.map((entry, i) => (
              <Flex key={`${entry.title}-${i}`} style={{ columnGap: '10px' }}>
                <Flex flexDirection="column" alignItems="center" flexShrink={0} w="7px">
                  <Box w="7px" h="7px" borderRadius="50%" bg={i === 0 ? visual.text : '#DFE4EA'} mt="4px" />
                  {i < preview.timeline.length - 1 && <Box flex="1" w="1px" bg={notifTokens.hairline} minH="24px" />}
                </Flex>
                <Box pb={i < preview.timeline.length - 1 ? '14px' : 0} flex="1" minW="0">
                  <Flex alignItems="baseline" style={{ columnGap: '8px' }}>
                    <Text fontSize="12.5px" fontWeight={500} color={notifTokens.ink}>
                      {entry.title}
                    </Text>
                    <Text fontSize="11.5px" color={notifTokens.faint}>
                      {entry.at}
                    </Text>
                  </Flex>
                  <Text fontSize="12px" lineHeight="17px" color={notifTokens.muted} mt="2px">
                    {entry.text}
                  </Text>
                </Box>
              </Flex>
            ))}
          </Box>
        )}
      </Box>
    </Flex>
  )
}
